// ============================================================================
// CLEO'S SECRET — PathologyUpload
// File: src/pages/PathologyUpload.jsx
//
// Patient-facing transactional surface. Three modes (PDF / image / paste),
// then form, then two-stage loading (upload → interpretation), then
// editorial confirmation.
//
// Inherits the transactional pattern from CleoScheduler but simpler:
// single-step form rather than a wizard, and the loading states carry
// more weight because the patient is waiting on AI interpretation.
//
// Preserved logic:
//   • /api/upload-pathology endpoint and payload unchanged
//   • /api/interpret-pathology chained after upload completes
//   • 10MB file size guard
//   • PDF / image / pasted text modes with separate accept attributes
//   • Optional collection date + laboratory metadata
//   • Stage state machine: input → uploading → interpreting → complete
//   • fileToBase64 helper preserved
// ============================================================================

import React, { useState, useRef } from 'react';
import {
  FF, FS, P,
  Eyebrow,
  Spinner,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Selectable mode card ─────────
function ModeCard({ selected, onClick, children }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: 'clamp(20px, 2.2vw, 26px)',
        marginBottom: 12,
        background: selected ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
        border: '1px solid ' + (selected ? P.GO : P.SAND3),
        borderLeft: '3px solid ' + (selected ? P.GO : P.SAND3),
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onMouseEnter={function(e) {
        if (!selected) e.currentTarget.style.background = 'rgba(232,221,196,0.4)';
      }}
      onMouseLeave={function(e) {
        if (!selected) e.currentTarget.style.background = P.PAP;
      }}>
      {children}
    </div>
  );
}

// ───────── Reusable form field label ─────────
function FieldLabel({ children, optional }) {
  return (
    <label style={{
      display: 'block',
      fontFamily: FF,
      fontSize: 'clamp(10px, 0.85vw, 11px)',
      fontWeight: 700,
      letterSpacing: '0.28em',
      textTransform: 'uppercase',
      color: P.BR2,
      marginBottom: 8,
    }}>
      {children}
      {optional && (
        <span style={{
          fontFamily: FF,
          fontStyle: 'italic',
          fontWeight: 400,
          textTransform: 'none',
          letterSpacing: '0.02em',
          color: P.TF,
          fontSize: '0.95em',
          marginLeft: 6,
        }}>(optional)</span>
      )}
    </label>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// PathologyUpload
// ═════════════════════════════════════════════════════════════════════════
export default function PathologyUpload(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;

  var modeState = useState('select');
  var mode = modeState[0];
  var setMode = modeState[1];

  var fileState = useState(null);
  var file = fileState[0];
  var setFile = fileState[1];

  var pastedTextState = useState('');
  var pastedText = pastedTextState[0];
  var setPastedText = pastedTextState[1];

  var collectionDateState = useState('');
  var collectionDate = collectionDateState[0];
  var setCollectionDate = collectionDateState[1];

  var laboratoryState = useState('');
  var laboratory = laboratoryState[0];
  var setLaboratory = laboratoryState[1];

  var uploadingState = useState(false);
  var uploading = uploadingState[0];
  var setUploading = uploadingState[1];

  var stageState = useState('input');
  var stage = stageState[0];
  var setStage = stageState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var fileInputRef = useRef(null);

  function handleFileSelect(e) {
    var f = e.target.files[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError('File is too large. The maximum size is 10 MB.');
      return;
    }
    setError(null);
    setFile(f);
  }

  function fileToBase64(f) {
    return new Promise(function(resolve, reject) {
      var reader = new FileReader();
      reader.onload = function() { resolve(reader.result); };
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });
  }

  function submit() {
    if (!email) {
      setError('Email is required.');
      return;
    }
    if (mode === 'text' && !pastedText.trim()) {
      setError('Please paste your pathology results.');
      return;
    }
    if ((mode === 'pdf' || mode === 'image') && !file) {
      setError('Please select a file.');
      return;
    }

    setUploading(true);
    setError(null);
    setStage('uploading');

    var prepareData = (mode === 'text')
      ? Promise.resolve({ fileData: null, fileType: 'text', fileName: null })
      : fileToBase64(file).then(function(b64) {
        return { fileData: b64, fileType: mode, fileName: file.name };
      });

    prepareData
      .then(function(prepared) {
        return fetch('/api/upload-pathology', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            fileType: prepared.fileType,
            fileData: prepared.fileData,
            fileName: prepared.fileName,
            pastedText: mode === 'text' ? pastedText : null,
            collectionDate: collectionDate || null,
            laboratory: laboratory || null,
          }),
        });
      })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Upload failed'); });
        return res.json();
      })
      .then(function(data) {
        setStage('interpreting');
        return fetch('/api/interpret-pathology', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uploadId: data.uploadId }),
        });
      })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Interpretation failed'); });
        return res.json();
      })
      .then(function() {
        setStage('complete');
        setUploading(false);
      })
      .catch(function(err) {
        setError(err.message);
        setStage('input');
        setUploading(false);
      });
  }

  // ════════════════════════════════════════════════════════════════════════
  // LOADING STATES (uploading / interpreting)
  // ════════════════════════════════════════════════════════════════════════
  if (stage === 'uploading' || stage === 'interpreting') {
    var isUploading = stage === 'uploading';
    return (
      <div style={{
        fontFamily: FF,
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(40px, 6vw, 80px) clamp(24px, 3vw, 40px)',
      }}>
        <div style={{ maxWidth: 540, width: '100%', textAlign: 'center' }}>

          <div style={{ marginBottom: 'clamp(28px, 3.4vw, 40px)' }}>
            <Spinner color={P.GO} size={56} />
          </div>

          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 18,
          }}>{isUploading ? 'Uploading' : 'Reading your results'}</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(26px, 3vw, 40px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.15,
            letterSpacing: '-0.012em',
            margin: '0 0 clamp(18px, 2vw, 24px)',
          }}>
            {isUploading
              ? <>Securing <span style={{ fontStyle: 'italic', color: P.BR2 }}>your file.</span></>
              : <>Cleo is <span style={{ fontStyle: 'italic', color: P.BR2 }}>interpreting</span> your pathology.</>}
          </h1>

          <div style={{
            width: 'clamp(140px, 14vw, 180px)',
            height: 1,
            background: P.GO,
            margin: '0 auto clamp(24px, 2.8vw, 32px)',
            opacity: 0.85,
          }} />

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            color: P.TL,
            lineHeight: 1.85,
            maxWidth: 460,
            margin: '0 auto',
          }}>
            {isUploading
              ? 'Your results are being uploaded securely.'
              : 'This usually takes twenty to thirty seconds. Your markers are being extracted, and a personalised explanation is being prepared.'}
          </p>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // COMPLETE STATE
  // ════════════════════════════════════════════════════════════════════════
  if (stage === 'complete') {
    return (
      <div style={{
        fontFamily: FF,
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(40px, 6vw, 80px) clamp(24px, 3vw, 40px)',
      }}>
        <div style={{ maxWidth: 600, width: '100%', textAlign: 'center' }}>

          {/* Ankh seal — canonical page-opener for completion state */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 'clamp(28px, 3vw, 40px)',
          }}>
            <div style={{ width: 'clamp(60px, 7vw, 80px)' }}>
              <Ankh responsive="closing" color={P.GO} />
            </div>
          </div>

          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 18,
          }}>Received</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(30px, 3.6vw, 52px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.1,
            letterSpacing: '-0.015em',
            margin: '0 0 clamp(18px, 2vw, 24px)',
          }}>Your results are with <span style={{ fontStyle: 'italic', color: P.BR2 }}>your GP.</span></h1>

          <div style={{
            width: 'clamp(140px, 14vw, 180px)',
            height: 1,
            background: P.GO,
            margin: '0 auto clamp(28px, 3vw, 36px)',
          }} />

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(17px, 1.5vw, 20px)',
            color: P.TX,
            lineHeight: 1.7,
            maxWidth: 500,
            margin: '0 auto 14px',
          }}>Your pathology has been read, and a personalised interpretation has been prepared.</p>

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            color: P.TL,
            lineHeight: 1.85,
            maxWidth: 500,
            margin: '0 auto clamp(32px, 3.6vw, 48px)',
          }}>Your GP will review the interpretation before it is released, usually within twenty-four hours. You will be notified when it is ready to view in your portal.</p>

          <button
            onClick={function() { if (onNavigate) onNavigate('portal'); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '16px 40px',
              background: P.INK,
              color: P.PAP,
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = P.BR; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = P.INK; }}>
            Back to your portal
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // INPUT STATE — mode selector or form
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: FF, background: P.PAP, minHeight: '100vh' }}>

      {/* ═════════ Header strip ═════════ */}
      <header style={{
        background: P.SAND,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 3vw, 40px) clamp(28px, 3.5vw, 48px)',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <div style={{ marginBottom: 16 }}>
          <Eyebrow center>Pathology</Eyebrow>
        </div>
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(28px, 3.4vw, 44px)',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.12,
          letterSpacing: '-0.012em',
          margin: 0,
        }}>Upload your <span style={{ fontStyle: 'italic', color: P.BR2 }}>results.</span></h1>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(13px, 1.1vw, 15px)',
          fontStyle: 'italic',
          color: P.BR2,
          marginTop: 10,
          letterSpacing: '0.02em',
        }}>Private and secure. Reviewed by your GP before you see it.</div>
      </header>

      {/* ═════════ Main canvas ═════════ */}
      <main style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3vw, 40px) clamp(48px, 6vw, 80px)',
      }}>

        {/* ─── MODE SELECTOR ─── */}
        {mode === 'select' && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 14,
              marginBottom: 'clamp(18px, 2.2vw, 26px)',
              paddingBottom: 'clamp(12px, 1.4vw, 16px)',
              borderBottom: '1px solid ' + P.SAND3,
            }}>
              <span style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: P.BR2,
              }}>How would you like to share?</span>
            </div>

            <ModeCard onClick={function() { setMode('pdf'); }}>
              <h3 style={{
                fontFamily: FF,
                fontSize: 'clamp(18px, 1.6vw, 22px)',
                fontWeight: 500,
                color: P.INK,
                lineHeight: 1.2,
                letterSpacing: '-0.005em',
                margin: '0 0 6px',
              }}>Upload a PDF</h3>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 15px)',
                color: P.TL,
                lineHeight: 1.6,
                margin: 0,
              }}>Most pathology results come as a PDF from your lab.</p>
            </ModeCard>

            <ModeCard onClick={function() { setMode('image'); }}>
              <h3 style={{
                fontFamily: FF,
                fontSize: 'clamp(18px, 1.6vw, 22px)',
                fontWeight: 500,
                color: P.INK,
                lineHeight: 1.2,
                letterSpacing: '-0.005em',
                margin: '0 0 6px',
              }}>Upload a photo</h3>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 15px)',
                color: P.TL,
                lineHeight: 1.6,
                margin: 0,
              }}>A JPG or PNG screenshot of your results.</p>
            </ModeCard>

            <ModeCard onClick={function() { setMode('text'); }}>
              <h3 style={{
                fontFamily: FF,
                fontSize: 'clamp(18px, 1.6vw, 22px)',
                fontWeight: 500,
                color: P.INK,
                lineHeight: 1.2,
                letterSpacing: '-0.005em',
                margin: '0 0 6px',
              }}>Paste the text</h3>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 15px)',
                color: P.TL,
                lineHeight: 1.6,
                margin: 0,
              }}>Copy and paste your results directly.</p>
            </ModeCard>
          </div>
        )}

        {/* ─── FORM (after mode selection) ─── */}
        {mode !== 'select' && (
          <div>

            {/* Change-method link */}
            <button
              onClick={function() {
                setMode('select');
                setFile(null);
                setPastedText('');
                setError(null);
              }}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 600,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: P.BR2,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginBottom: 'clamp(20px, 2.4vw, 28px)',
                padding: 0,
              }}>
              &larr; Change method
            </button>

            {/* PDF or image upload */}
            {(mode === 'pdf' || mode === 'image') && (
              <div style={{ marginBottom: 'clamp(20px, 2.4vw, 28px)' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={mode === 'pdf' ? 'application/pdf' : 'image/*'}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                {!file ? (
                  <div
                    onClick={function() { fileInputRef.current.click(); }}
                    style={{
                      padding: 'clamp(40px, 5vw, 60px) clamp(24px, 3vw, 40px)',
                      border: '2px dashed ' + P.GO,
                      background: 'rgba(232,221,196,0.3)',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={function(e) {
                      e.currentTarget.style.background = 'rgba(201, 169, 97, 0.06)';
                      e.currentTarget.style.borderColor = P.BR;
                    }}
                    onMouseLeave={function(e) {
                      e.currentTarget.style.background = 'rgba(232,221,196,0.3)';
                      e.currentTarget.style.borderColor = P.GO;
                    }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(18px, 1.6vw, 22px)',
                      fontWeight: 500,
                      color: P.INK,
                      lineHeight: 1.3,
                      letterSpacing: '-0.005em',
                      marginBottom: 8,
                    }}>
                      Click to choose {mode === 'pdf' ? 'a PDF' : 'an image'}
                    </div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(10px, 0.85vw, 11px)',
                      fontWeight: 700,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: P.BR2,
                    }}>Maximum 10 MB</div>
                  </div>
                ) : (
                  <div style={{
                    padding: 'clamp(20px, 2.2vw, 26px)',
                    background: 'rgba(201, 169, 97, 0.06)',
                    border: '1px solid ' + P.GO,
                    borderLeft: '3px solid ' + P.GO,
                  }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(10px, 0.85vw, 11px)',
                      fontWeight: 700,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: P.BR2,
                      marginBottom: 10,
                    }}>Selected file</div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(16px, 1.4vw, 18px)',
                      fontWeight: 500,
                      color: P.INK,
                      lineHeight: 1.3,
                      letterSpacing: '-0.005em',
                      marginBottom: 6,
                      wordBreak: 'break-word',
                    }}>{file.name}</div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(13px, 1.1vw, 14px)',
                      fontStyle: 'italic',
                      color: P.BR2,
                      marginBottom: 14,
                    }}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                    <button
                      onClick={function() { setFile(null); fileInputRef.current.value = ''; }}
                      style={{
                        fontFamily: FS,
                        fontSize: 'clamp(11px, 0.9vw, 12px)',
                        fontWeight: 600,
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                        color: P.RED,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}>
                      Remove and choose another
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Pasted text textarea */}
            {mode === 'text' && (
              <div style={{ marginBottom: 'clamp(20px, 2.4vw, 28px)' }}>
                <FieldLabel>Paste your results</FieldLabel>
                <textarea
                  value={pastedText}
                  onChange={function(e) { setPastedText(e.target.value); }}
                  rows={8}
                  placeholder={'Paste the full text of your pathology results here, including all markers, values, units and reference ranges\u2026'}
                  style={{
                    width: '100%',
                    padding: '16px 18px',
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    color: P.INK,
                    background: P.PAP,
                    border: '1px solid ' + P.SAND3,
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    lineHeight: 1.7,
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
                  onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
                />
              </div>
            )}

            {/* Collection date */}
            <div style={{ marginBottom: 'clamp(16px, 1.8vw, 20px)' }}>
              <FieldLabel optional>Collection date</FieldLabel>
              <input
                type="date"
                value={collectionDate}
                onChange={function(e) { setCollectionDate(e.target.value); }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontFamily: FF,
                  fontSize: 'clamp(14px, 1.2vw, 16px)',
                  color: P.INK,
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s',
                }}
                onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
                onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
              />
            </div>

            {/* Laboratory */}
            <div style={{ marginBottom: 'clamp(24px, 2.6vw, 32px)' }}>
              <FieldLabel optional>Laboratory</FieldLabel>
              <input
                type="text"
                value={laboratory}
                onChange={function(e) { setLaboratory(e.target.value); }}
                placeholder="e.g. Melbourne Pathology"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontFamily: FF,
                  fontSize: 'clamp(14px, 1.2vw, 16px)',
                  color: P.INK,
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s',
                }}
                onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
                onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: 'clamp(14px, 1.6vw, 18px) clamp(16px, 1.8vw, 22px)',
                background: P.RED_BG,
                border: '1px solid ' + P.RED,
                borderLeft: '3px solid ' + P.RED,
                marginBottom: 'clamp(18px, 2vw, 24px)',
                fontFamily: FF,
                fontSize: 'clamp(13px, 1.1vw, 15px)',
                color: P.RED,
                fontStyle: 'italic',
                lineHeight: 1.6,
              }}>{error}</div>
            )}

            {/* Submit */}
            <button
              onClick={submit}
              disabled={uploading}
              style={{
                width: '100%',
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '16px 28px',
                background: uploading ? 'rgba(26,15,8,0.3)' : P.INK,
                color: P.PAP,
                border: 'none',
                cursor: uploading ? 'default' : 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={function(e) { if (!uploading) e.currentTarget.style.background = P.BR; }}
              onMouseLeave={function(e) { if (!uploading) e.currentTarget.style.background = P.INK; }}>
              Send to Cleo
            </button>

            {/* Workflow note */}
            <div style={{
              padding: 'clamp(18px, 2vw, 24px) clamp(20px, 2.2vw, 28px)',
              background: 'rgba(201, 169, 97, 0.06)',
              borderLeft: '3px solid ' + P.GO,
              marginTop: 'clamp(20px, 2.4vw, 28px)',
            }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(10px, 0.85vw, 11px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 10,
              }}>What happens next</div>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 15px)',
                color: P.TL,
                lineHeight: 1.7,
                margin: 0,
              }}>
                Cleo will read your results and prepare a plain-English explanation. Your GP will review the interpretation before it is released to you, usually within twenty-four hours. You will see it in your portal when ready.
              </p>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
